import { useState } from 'react'
import { Link, useNavigate } from 'react-router'
import type { JSX } from 'react'
import { api, call } from '@/api/client'
import { useMutation, useQuery } from '@/api/query'
import { formatDate } from '@/lib/format'
import { idempotencyKey } from '@/lib/idempotency'
import { may, useActiveTenant } from '@/tenant/context'
import { Button } from '@/ui/Button'
import { Field } from '@/ui/Field'
import { Empty, ErrorState, InlineError, Loading } from '@/ui/states'
import { useToast } from '@/ui/toast-context'

/** The six reasons `admin#cancel` offers. Optional, and never required. */
export const REASONS = [
  'Too expensive',
  "Didn't see results",
  'Too complicated',
  'Hired an agency',
  'Business closed or paused',
  'Something else',
] as const

/**
 * `admin#cancel` — a screen, not a modal (ADM-18, ADM-35…38).
 *
 * **It was a `Modal`, and four separate parity rows were the same mistake.** The
 * approved screen is an `.ar-card` in a 38rem column: radius `--ar-radius-xl`,
 * an `h3` title, body text in `--ar-text`, and a footer with a top rule. What
 * shipped was `.ar-modal__panel` — 34rem, a 28px radius, an `h2` at 1.5rem, a
 * body forced to `--ar-text-muted` so the "What happens" heading and the field
 * label both rendered grey, and a footer with no rule and no spacer. Restyling
 * `Modal` would have moved every other dialog in the product; rendering the
 * screen the design actually draws moves only this one.
 *
 * **And it is a route**, which is the other half of ADM-18: `admin.html`'s own
 * rail lists Cancellation as a screen, and a dialog cannot be linked to, sent to
 * a colleague who holds the billing permission, or returned to with a back
 * button. Cancelling a subscription is not a confirmation — it is a decision
 * somebody takes with a page open in front of them.
 *
 * **No retention maze** — that is the design's own note, and it survives the
 * move. The pause offer is made once (ADM-16) and it is a link, not a step.
 */
export function CancelPage(): JSX.Element {
  const { tenant, membership } = useActiveTenant()
  const toast = useToast()
  const navigate = useNavigate()
  const manages = may(membership, 'billing:manage')
  const [reason, setReason] = useState<string>(REASONS[0])

  const subscription = useQuery(
    (signal) => call(() => api.GET('/api/v1/billing/subscription', { signal })),
    [],
  )
  const cancel = useMutation((why: string) =>
    call(() =>
      api.POST('/api/v1/billing/subscription/cancellation', {
        body: { reason: why },
        params: { header: { 'Idempotency-Key': idempotencyKey('cancel', tenant.id) } },
      }),
    ),
  )

  if (subscription.state.status === 'loading') return <Loading label="Loading your subscription" />
  if (subscription.state.status === 'error') {
    return <ErrorState failure={subscription.state.failure} onRetry={subscription.refetch} />
  }

  const current = subscription.state.data.subscription ?? null

  if (current === null || !manages) {
    // Two different reasons for one state, and each is stated rather than
    // rendering an inert form: there is nothing to cancel, or this member is
    // not the one who can. The API refuses either way; this is what the person
    // in front of the screen needs to know.
    return (
      <div style={{ maxWidth: '38rem', margin: '0 auto' }}>
        <Empty
          title={current === null ? 'No subscription to cancel' : 'Only the owner can cancel'}
          body={
            current === null
              ? 'This organisation has no live subscription.'
              : 'Ask the organisation’s owner — cancelling is theirs to do, as is starting or changing a plan.'
          }
          action={
            <Link className="ar-btn ar-btn--ghost" to="/billing">
              Back to billing
            </Link>
          }
        />
      </div>
    )
  }

  return (
    <div style={{ maxWidth: '38rem', margin: '0 auto' }}>
      <div className="ar-card">
        <div className="ar-card__header">
          <h2>Cancel your subscription</h2>
        </div>
        <div className="ar-card__body ar-stack">
          <div className="ar-panel">
            <strong>What happens</strong>
            <ul
              className="ar-muted"
              style={{
                margin: 'var(--ar-space-2) 0 0',
                paddingLeft: 'var(--ar-space-5)',
                fontSize: 'var(--ar-text-body-sm-size)',
                display: 'grid',
                gap: 'var(--ar-space-2)',
              }}
            >
              <li>
                You keep full access until{' '}
                <strong>
                  {current.current_period_end
                    ? formatDate(current.current_period_end)
                    : 'the end of this period'}
                </strong>{' '}
                — the period you&rsquo;ve paid for.
              </li>
              <li>Everything we published stays on your site. It&rsquo;s yours.</li>
              <li>Your rankings don&rsquo;t reset — we just stop working on them.</li>
              {/* ADM-17 / NC-4. The mockup's fifth bullet promised "we keep your
                  data for 90 days so you can come back". There is no such
                  promise: nothing in `app/db/erasure.py` or the retention policy
                  states a window, and a screen is the wrong place to invent one.
                  The line is retired rather than implemented. */}
              <li>No cancellation fee. No further charges.</li>
            </ul>
          </div>

          <Field label="Why are you leaving? (optional)">
            {(props) => (
              <select
                className="ar-select"
                id={props.id}
                aria-describedby={props['aria-describedby']}
                value={reason}
                onChange={(event) => {
                  setReason(event.target.value)
                }}
              >
                {REASONS.map((option) => (
                  <option key={option}>{option}</option>
                ))}
              </select>
            )}
          </Field>

          {/* ADM-16. Offered once, as the design's own note requires — "presented
              once, not as an obstacle". It is a link to the control that already
              exists rather than a second confirmation step, and it is honest
              about what pausing does not do: the subscription carries on
              billing, because there is no cheaper paused tier to move to. */}
          <div className="ar-alert ar-alert--info">
            <span className="ar-alert__icon" aria-hidden="true">
              ◷
            </span>
            <div className="ar-alert__body">
              <strong>Seasonal business?</strong> Settings → Danger zone has &ldquo;Pause all
              automation&rdquo;. Nothing runs, so your usage spend drops to nothing, and your
              rankings, content and leads all stay where they are. The subscription itself carries
              on billing — we don&rsquo;t have a cheaper paused tier.{' '}
              <Link className="ar-link" to="/settings/danger-zone">
                Pause automation instead
              </Link>
            </div>
          </div>

          <InlineError failure={cancel.state.failure} />
        </div>
        <div className="ar-card__footer">
          <Link className="ar-btn ar-btn--ghost" to="/billing">
            Keep my subscription
          </Link>
          <span className="ar-card__spacer" />
          <Button
            tone="danger"
            disabled={cancel.pending}
            busy={cancel.pending}
            onClick={() => {
              void cancel.run(reason).then((result) => {
                if (!result.ok) return
                toast.show({
                  kind: 'success',
                  title: 'Your subscription will end at the end of this period.',
                })
                void navigate('/billing')
              })
            }}
          >
            Cancel subscription
          </Button>
        </div>
      </div>
    </div>
  )
}
